import { Component, ViewChild, Output, EventEmitter, OnInit, Input } from '@angular/core';
import { ModalComponent } from '../../../components/modal/modal.component';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ToastService } from '../../../components/toast/toast.service';
import { UploadingFile } from '../uploader.component';
import { SearchService } from '../../../services/search.service';
import Utils from '../../../services/utils';
import * as _ from 'underscore';
import { MatSelectChange } from '@angular/material/select/typings';
import * as moment from 'moment';
import { PatientService } from '../../../services/patient.service';

@Component({
  selector: 'lowy-image-meta',
  template: `
  <lowy-modal #modal>
    <div class="form">
      <div class="headline">Edit Image Metadata</div>
      <div class="subheading">After adding the image's metadata, please hit the save button to move the file into your complete queue.
      </div>
      <div class="seperator"></div>
      <div class="icon-label-row">
        <div class="data-icon-clear image"><i class="material-icons">image</i></div>
        <div class="headline">{{file?.fileName}}</div>
      </div>
      <div *ngIf="file?.status == 'denied'">
        <div *ngIf="file?.rejectionReasons?.folderReasons && file?.rejectionReasons?.folderReasons.length > 0">
          Folder Rejection Reasons
          <ul class="standard">
            <li *ngFor="let reason of file?.rejectionReasons?.folderReasons">
              {{reason}}
            </li>
          </ul>
        </div>
        <div *ngIf="file?.rejectionReasons?.fileReasons && file?.rejectionReasons?.fileReasons.length > 0">
          File Rejection Reasons
          <ul class="standard">
            <li *ngFor="let reason of file?.rejectionReasons?.fileReasons">
              {{reason}}
            </li>
          </ul>
        </div>
        <div *ngIf="file?.rejectionReasons?.comments">
          Rejection Comments: {{file?.rejectionReasons?.comments}}
        </div>
      </div>
      <form [formGroup]="imageMetaForm" (ngSubmit)="save()">
        <mat-form-field>
          <input matInput placeholder="Patient ID" formControlName="patientId" type="text">
        </mat-form-field>
        <mat-form-field>
          <input matInput placeholder="Site" formControlName="site" type="text" (blur)="siteIdBlur()">
          <mat-hint class="mat-hint-error" *ngIf="siteIdErr">{{siteIdErr}}</mat-hint>
        </mat-form-field>
        <span class="input-error-message"
          *ngIf="imageMetaForm.get('site').touched && imageMetaForm.get('site').invalid">Site is required</span>
        <mat-form-field>
          <input matInput formControlName="datePhotoTaken" [matDatepicker]="picker" placeholder="Date photo taken">
          <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
        </mat-form-field>
        <span class="input-error-message" *ngIf="imageMetaForm.get('datePhotoTaken').touched &&
          imageMetaForm.get('datePhotoTaken').invalid">Date photo taken is required</span>
        <mat-form-field>
          <mat-select placeholder="Eye" formControlName="eye" >
            <mat-option *ngFor="let eye of eyes" [value]="eye">
              {{eye}}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field>
          <mat-select placeholder="Image type" formControlName="imageType" (selectionChange)="imageTypeSelected($event)">
            <mat-option *ngFor="let imageType of imageTypes" [value]="imageType">
              {{imageType}}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field>
          <mat-select placeholder="Image platform" formControlName="imagePlatform">
            <mat-option *ngFor="let imagePlatform of imagePlatforms" [value]="imagePlatform">
              {{imagePlatform}}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field>
          <mat-select placeholder="Study ID" formControlName="studyId">
            <mat-option *ngFor="let studyId of studyIds" [value]="studyId">
              {{studyId}}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field>
          <textarea maxlength="400" matInput placeholder="Comments for this single image" formControlName="comments"></textarea>
        </mat-form-field>
      </form>
      <div class="seperator"></div>
      <div class="button-row">
        <span (click)="cancel()">cancel</span>
        <span (click)="imageMetaForm.valid && save()"
          [ngClass]="{disabled: imageMetaForm.invalid}">{{saveText}}</span>
      </div>
    </div>
  </lowy-modal>
  `,
  providers: [ SearchService, PatientService ]
})
export class ImageMetaComponent implements OnInit {
  public imageMetaForm: FormGroup;
  public file: UploadingFile;

  @ViewChild('modal') modal: ModalComponent;

  @Output() onSave: EventEmitter<any>;
  @Input('saveText') saveText: string;

  public eyes: Array<string>;
  public imageTypes: Array<string>;
  public imagePlatforms: Array<string>;
  public studyIds: Array<string>;

  public siteIdErr: string;

  private previouslySavedForm: {
    site: string,
    datePhotoTaken: Date,
    eye: string,
    imageType: string,
    imagePlatform: string,
    studyId: string
  };

  constructor(private formBuilder: FormBuilder,
    private toastService: ToastService,
    private searchService: SearchService,
    private patientService: PatientService) {
    this.imageMetaForm = this.formBuilder.group({
      patientId: [''],
      site: [''],
      datePhotoTaken: [''],
      eye: [''],
      imageType: [''],
      imagePlatform: [''],
      studyId: [''],
      comments: ['']
    });

    this.onSave = new EventEmitter();
  }

  async ngOnInit() {
    this.eyes = ['OD', 'OS', 'OU'];
    this.imageTypes = [
      'FP- Fundus Photo',
      'RF- Red Free',
      'FA- Fluorescein Angiography',
      'OCT',
      'FAF- Fundus Autofluoresence',
      'BLR-Blue light reflectance',
      'IR-Infrared',
      'DWAF',
      'MP-Microperimetry',
      'OCT-A',
      'FLIO',
      'CCM'
    ];
    this.imagePlatforms = [ ];
    this.studyIds = [ ];
  }

  public async open(file: UploadingFile) {
    this.file = file;
    this.buildForm();
    // wait for self to be closed and hidden, because parent caller will sometimes try to open me up too fast
    while (this.modal.visible) {
      await Utils.sleep(150);
    }
    this.modal.open();
  }

  async buildForm() {
    // stuff to do after we have the file
    const studyIdInfo = await this.patientService.getStudyIdsForPatientId(this.file.patientId);
    this.studyIds = studyIdInfo.studyIds;
    const studyIdToPrefill = studyIdInfo.studyIdToPrefill;

    this.imageMetaForm = this.formBuilder.group({
      patientId: [{ value: this.file.patientId, disabled: true }, Validators.required],
      site: [this.file['site'], Validators.required],
      datePhotoTaken: [this.file['datePhotoTaken'], Validators.required],
      eye: [this.file['eye'], Validators.required],
      imageType: [this.file['imageType'], Validators.required],
      imagePlatform: [null, Validators.required],
      studyId: [this.file['studyId'] ? this.file['studyId'] : studyIdToPrefill, Validators.required],
      comments: null
    });
    if (this.file['imageType'] && this.file['imagePlatform']) {
      this.imageTypeSelected({ value: this.file['imageType'] } as MatSelectChange);
      this.imageMetaForm.patchValue( { imagePlatform: this.file['imagePlatform'] });
    }

    if (this.previouslySavedForm) {
      this.imageMetaForm.setValue({
        patientId: this.file.patientId,
        site: this.previouslySavedForm.site,
        datePhotoTaken: this.previouslySavedForm.datePhotoTaken,
        eye: this.previouslySavedForm.eye,
        imageType: this.previouslySavedForm.imageType,
        imagePlatform: this.previouslySavedForm.imagePlatform,
        studyId: this.studyIds.includes(this.previouslySavedForm.studyId) ? this.previouslySavedForm.studyId : studyIdToPrefill,
        comments: null
      });
    }
  }

  async save() {
    try {
      this.file.meta = {
        'SITE': this.imageMetaForm.value.site,
        'PHOTO_DATE': this.imageMetaForm.value.datePhotoTaken,
        'EYE': this.imageMetaForm.value.eye,
        'IMAGE_TYPE': this.imageMetaForm.value.imageType,
        'IMAGE_PLATFORM': this.imageMetaForm.value.imagePlatform,
        'STUDY_ID': this.imageMetaForm.value.studyId,
        'UPLOAD_DATE': moment(),
        'COMMENTS': this.imageMetaForm.value.comments
      };
      this.previouslySavedForm = {
        site: this.imageMetaForm.value.site,
        datePhotoTaken: this.imageMetaForm.value.datePhotoTaken,
        eye: this.imageMetaForm.value.eye,
        imageType: this.imageMetaForm.value.imageType,
        imagePlatform: this.imageMetaForm.value.imagePlatform,
        studyId: this.imageMetaForm.value.studyId
      };
      this.onSave.emit(this.file);
      this.modal.close();
    }
    catch (err) {
      this.modal.close();
      this.toastService.throwToast('Error saving image data');
    }
  }

  // we restrict imagePlatform based on selected imageType
  // tslint:disable:max-line-length
  public imageTypeSelected(imageType: MatSelectChange) {
    switch (imageType.value) {
      case 'FP- Fundus Photo': this.imagePlatforms = ['Fundus Camera']; break;
      case 'RF- Red Free': this.imagePlatforms = ['Fundus Camera', 'Heidelberg Spectralis']; break;
      case 'FA- Fluorescein Angiography': this.imagePlatforms = ['Fundus Camera',	'Heidelberg Spectralis', 'Heidelberg HRA2', 'Heidelberg HRA Classic', 'Other']; break;
      case 'OCT': this.imagePlatforms = ['Heidelberg Spectralis', 'Zeiss Cirrus', 'Topcon 3D-OCT1000', 'Topcon 3D-OCT2000', 'Topcon Triton', 'Other']; break;
      case 'FAF- Fundus Autofluoresence': this.imagePlatforms = ['Heidelberg Spectralis', 'Heidelberg HRA2', 'Heidelberg HRA Classic', 'Fundus Camera', 'Other']; break;
      case 'BLR-Blue light reflectance': this.imagePlatforms = ['Heidelberg Spectralis', 'Heidelberg HRA2', 'Heidelberg HRA Classic', 'Fundus Camera', 'Other']; break;
      case 'IR-Infrared': this.imagePlatforms = ['Heidelberg Spectralis', 'Heidelberg HRA2', 'Heidelberg HRA Classic', 'Other']; break;
      case 'DWAF': this.imagePlatforms = ['Heidelberg HRA Classic', 'Heidelberg Spectralis', 'Other']; break;
      case 'MP-Microperimetry': this.imagePlatforms = ['Nidek MP-1', 'CenterVue Maia', 'Nidek MP-3', 'Other']; break;
      case 'OCT-A': this.imagePlatforms = ['Heidelberg Spectralis', 'Optovue AgioVue', 'Zeiss Angioplex', 'Zeiss Plex Elite 9000', 'Other']; break;
      case 'FLIO': this.imagePlatforms = ['Heidelberg FLIO']; break;
      case 'CCM': this.imagePlatforms = ['HRT III Rostock module']; break;
    }
  }

  public clearSaved() {
    this.previouslySavedForm = null;
  }

  cancel() {
    this.modal.close();
  }

  public async siteIdBlur() {
    this.siteIdErr = null;
    if (!this.site.value || this.site.value === '') {
      this.siteIdErr = 'Site ID is required for image upload';
    }
    else {
      try {
        const site = await this.patientService.testAndNormalizeSiteId(this.site.value);
        this.site.setValue(site);
      }
      catch (err) {
        this.siteIdErr = err.message || 'Invalid Patient ID';
      }
    }
  }

  get site() { return this.imageMetaForm.get('site'); }
}



// WEBPACK FOOTER //
// ./src/app/home/uploader/modals/image-meta-modal.component.ts